import {
  ArrowsInIcon,
  CircleNotchIcon,
  MagnifyingGlassIcon,
  MoonStarsIcon,
  SunDimIcon,
} from "@phosphor-icons/react";
import Placeholder from "@tiptap/extension-placeholder";
import type { EditorView } from "@tiptap/pm/view";
import { type Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import IconButton from "@/components/ui/icon-button";
import { useFeature } from "@/lib/feature-management";
import { cn } from "@/lib/utils";
import AutoCloseBrackets from "./AutoCloseBracketsExtension";
import BooleanHighlight from "./BooleanHighlightExtension";
import type { InlineSearchEditorProps } from "./InlineSearchEditor";
import MatchingBracketHighlight from "./MatchingBracketHighlightExtension";
import { useEditorContentSync } from "./useEditorContentSync";

interface TiptapEditorProps {
  extensions?: NonNullable<Parameters<typeof useEditor>[0]>["extensions"];
  content?: string;
  editorProps?: NonNullable<Parameters<typeof useEditor>[0]>["editorProps"];
  className?: string;
  placeholder?: string;
  onUpdate?: (editor: Editor) => void;
  autoFocus?: NonNullable<Parameters<typeof useEditor>[0]>["autofocus"];
  onSearch?: (text: string) => void;
}

export interface ExpandedSearchEditorProps extends InlineSearchEditorProps {
  isSearching?: boolean;
  onUpdate?: (editor: Editor) => void;
}

/**
 * Base editor component with TipTap integration
 * @component
 * @param {Object} props - The component props
 * @param {Array} [props.extensions=[]] - Array of TipTap extensions to enhance editor functionality
 * @param {string} [props.content="Type here for boolean search"] - Initial content for the editor
 * @param {Object} [props.editorProps={}] - Additional editor configuration options
 * @param {string} [props.className] - Additional CSS classes to customize the editor's appearance
 * @param {string} [props.placeholder="Type Here for boolean search"] - Placeholder text shown when the editor is empty
 * @param {Function} [props.onSearch] - Function to handle search when Enter is pressed
 * @returns {JSX.Element} The rendered editor component
 */
const BaseEditor = forwardRef<Editor | null, TiptapEditorProps>(
  (
    {
      extensions = [],
      content = "Type here for boolean search",
      editorProps = {},
      className,
      placeholder = "Type Here for boolean search",
      onUpdate,
      autoFocus = false,
      onSearch,
    },
    ref,
  ) => {
    const editor = useEditor({
      extensions: [
        Placeholder.configure({
          placeholder,
          emptyEditorClass: cn(
            "cursor-text tracking-[0.12px] before:content-[attr(data-placeholder)]",
            "before:absolute before:opacity-50",
            "before-pointer-events-none",
          ),
        }),
        StarterKit.configure({
          bold: false,
          italic: false,
        }),
        BooleanHighlight,
        AutoCloseBrackets,
        MatchingBracketHighlight,
        ...extensions,
      ],
      autofocus: autoFocus,
      content,
      editorProps: {
        attributes: {
          class: cn(
            "text-xs font-sans tracking-[0.12px] rounded-md border border-gray-200 drop-shadow-xs p-3",
            "focus:outline-hidden focus:ring-blue-500 focus:border-blue-500",
            className,
          ),
        },
        handleKeyDown: (_view: EditorView, event: KeyboardEvent) => {
          if (event.key === "Enter") {
            event.preventDefault(); // Prevents new line creation
            if (onSearch && editor) {
              onSearch(editor.getText().trim());
            }
            return true; // Handled
          }
          if (event.key === "Tab") {
            // Don't prevent default tab behavior - let it navigate out of the editor
            return false; // Not handled, let browser handle tab
          }
          return false;
        },
        handlePaste: (view: EditorView, event: ClipboardEvent) => {
          event.preventDefault();
          const text = event.clipboardData?.getData("text/plain") || "";
          const { state, dispatch } = view;
          const { tr } = state;
          tr.insertText(text.trimStart(), tr.selection.from, tr.selection.to);
          dispatch(tr);
          return true;
        },
        ...editorProps,
      },
      onUpdate: ({ editor }) => {
        if (onUpdate) {
          onUpdate(editor);
        }
      },
    });

    // Sync editor content when content prop changes
    useEditorContentSync(editor, content);

    useImperativeHandle(ref, () => editor as Editor, [editor]);

    if (!editor) {
      return <p>Loading editor...</p>;
    }

    return <EditorContent editor={editor} />;
  },
);

// #region Boolean highlight dark theme

/** Wrap the editor in this class so the boolean keyword colors switch to the dark palette. */
export const BOOLEAN_HIGHLIGHT_DARK_WRAPPER_CLASS =
  "boolean-highlight--github-dark";

/** Pass to `BaseEditor` `className` so the editor surface switches to the dark palette. */
export const BOOLEAN_HIGHLIGHT_DARK_EDITOR_CLASS =
  "border-[#30363d] bg-[#0d1117] text-[#c9d1d9] shadow-none focus:border-[#58a6ff] focus:ring-[#58a6ff]";

/**
 * Light/dark theme state for boolean search editors, gated by the
 * `enable-boolean-highlight-dark-theme` flag. `themeToggle` is null when the flag is off.
 */
export const useBooleanHighlightDarkTheme = () => {
  const [isDarkSelected, setIsDarkSelected] = useState(false);
  const enableBooleanHighlightDarkTheme = useFeature(
    "enable-boolean-highlight-dark-theme",
    false,
  );

  useEffect(() => {
    if (!enableBooleanHighlightDarkTheme) {
      setIsDarkSelected(false);
    }
  }, [enableBooleanHighlightDarkTheme]);

  const themeToggle = enableBooleanHighlightDarkTheme ? (
    <IconButton
      icon={
        isDarkSelected ? <SunDimIcon size={16} /> : <MoonStarsIcon size={16} />
      }
      variant="icon-only"
      size="md"
      className="shrink-0"
      onPress={() => setIsDarkSelected((v) => !v)}
      aria-label={
        isDarkSelected ? "Use light editor theme" : "Use dark editor theme"
      }
      aria-pressed={isDarkSelected}
      tooltipLabel={isDarkSelected ? "Light editor" : "Dark editor"}
    />
  ) : null;

  return {
    isDarkActive: enableBooleanHighlightDarkTheme && isDarkSelected,
    themeToggle,
  };
};

// #region ExpandedSearchEditor

const ExpandedSearchEditor = ({
  handleSave,
  content,
  handleExpand: handleCollapse,
  handleSearch,
  isSearching = false,
  onUpdate,
}: ExpandedSearchEditorProps) => {
  const editorRef = useRef<Editor | null>(null);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const { isDarkActive, themeToggle } = useBooleanHighlightDarkTheme();

  const onSearch = () => {
    if (editorRef.current) {
      const plainText = editorRef.current.getText();
      handleSearch(plainText);
    }
  };

  return (
    <div className="mb-3 bg-white">
      <div className="flex items-center gap-2 py-2">
        <div className="flex min-w-0 flex-1 items-center">
          <MagnifyingGlassIcon size={14} className="text-gray-600" />
          <span className="ml-1 font-medium text-xs tracking-[0.12px] text-gray-700">
            Search
          </span>
        </div>
        {themeToggle}
      </div>
      <div className={cn(isDarkActive && BOOLEAN_HIGHLIGHT_DARK_WRAPPER_CLASS)}>
        <BaseEditor
          ref={editorRef}
          content={content}
          className={cn(
            "min-h-24",
            isDarkActive && BOOLEAN_HIGHLIGHT_DARK_EDITOR_CLASS,
          )}
          placeholder="Search..."
          onUpdate={(editor) => {
            onUpdate?.(editor);
            setIsEditorEmpty(editor.isEmpty);
            if (editor.isEmpty) {
              handleSearch("");
            }
          }}
          autoFocus={true}
          onSearch={onSearch}
        />
      </div>
      <div className="my-2 flex items-center justify-end gap-2 tracking-wide">
        <Button
          className="text-xs"
          variant={"ghost"}
          size={"sm"}
          onPressChange={handleCollapse}
        >
          <ArrowsInIcon className="mr-1 text-gray-600" size={14} />
          Close
        </Button>
        {handleSave && (
          <Button
            variant={"outline"}
            className="text-xs"
            onPressChange={() => {
              if (editorRef.current) {
                handleSave(editorRef.current);
              }
            }}
            size={"sm"}
            isDisabled={isEditorEmpty && content?.length === 0}
          >
            Save as key topic
          </Button>
        )}
        <Button
          className="text-xs flex items-center"
          onPressChange={onSearch}
          size={"sm"}
        >
          {isSearching && (
            <CircleNotchIcon size={16} className={"mr-1 animate-spin"} />
          )}
          <span> {isSearching ? "Searching" : "Search"}</span>
        </Button>
      </div>
    </div>
  );
};

export { ExpandedSearchEditor };
export default BaseEditor;
